import { useState } from 'react';
import {
  AttributeGroup,
  AttributeValue,
} from '../../../../api/protosCompiled/globalItem/globalItem_pb';
import { ItemService } from '../../../../api/GlobalItemService';
import { useFetchAttributeGroups } from '../../../../hooks/useFetchAttributeGroups';
import { useMutation, useQueryClient } from 'react-query';
import { useFetchUser } from '../../../../hooks/useFetchUsers';
import { useGetMasterFormatDivision } from '../../../../hooks/useGetMasterFormatDivision';
import { MasterFormatDivision } from '../../../../api/protosCompiled/masterFormatDivision/masterFormatDivision_pb';
import { filterAttributeGroups } from './helper';
import AttributeValuesTable from './AttributeValuesTable';
import { UB_AccountUser } from '../../../../api/protosCompiled/ub_users/ub_users_pb';
import AttributesTable from './AttributesTable';
import ManageAttributeDialog from './ManageAttributeDialog';
import {
  Dialog,
  DialogContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  Grid,
  useTheme,
} from '@mui/material';
import React from 'react';

export const handleError = (e: any) => {
  e.preventDefault();
  e.stopPropagation();
  alert('Error creating or updating attribute group');
};

const CreateAttribute = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { data: user } = useFetchUser();
  const { data: attributeGroups } = useFetchAttributeGroups();
  const { data: masterFormatDivision } = useGetMasterFormatDivision();

  // State variables
  const [newAttributeGroupName, setNewAttributeGroupName] = useState('');
  const [editingAttributeGroupId, setEditingAttributeGroupId] = useState<number | null>(null);
  const [selectedDivision, setSelectedDivision] = useState<MasterFormatDivision.AsObject | null>(
    null
  );
  const [filterDivision, setFilterDivision] = useState<string>('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAttributeValue, setSelectedAttributeValue] =
    useState<AttributeValue.AsObject | null>(null);

  const handleOpenDialog = (attributeValue: AttributeValue.AsObject | null) => {
    if (!editingAttributeGroupId) {
      alert('No attribute group is selected. Please select a group first.');
      return;
    }
    setSelectedAttributeValue({
      attributegroupid: editingAttributeGroupId,
      attributevalueid: attributeValue?.attributevalueid || 0,
      itemattributevaluemapid: attributeValue?.itemattributevaluemapid || 0,
      attributevaluename: attributeValue?.attributevaluename || '',
      attributevariablesList: attributeValue?.attributevariablesList || [],
      upc: attributeValue?.upc || '',
      attributegroupname: attributeValue?.attributegroupname || '',
    });
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedAttributeValue(null);
  };

  // Filtered attribute groups
  const filteredAttributeGroups = filterAttributeGroups(filterDivision, attributeGroups);

  // Find the selected attribute group
  const selectedAttributeGroup = attributeGroups?.find(
    group => group.attributegroupid === editingAttributeGroupId
  );

  // Extract attribute values
  const attributeValues = selectedAttributeGroup?.attributevaluesList || [];

  // Mutations
  const { mutateAsync: createOrUpdateAttributeGroupMutate } = useMutation(
    ItemService.createOrUpdateAttributeGroup,
    {
      onSuccess: response => {
        queryClient.invalidateQueries('attributeGroups');
        if (response && response.attributegroupid) {
          setEditingAttributeGroupId(response.attributegroupid);
        }
      },
    }
  );

  const { mutateAsync: deleteAttributeGroupMutate } = useMutation(
    ItemService.deleteAttributeGroup,
    {
      onSuccess: (_, attributeGroupId) => {
        queryClient.invalidateQueries('attributeGroups');
        if (editingAttributeGroupId === attributeGroupId) {
          setEditingAttributeGroupId(null);
          setNewAttributeGroupName('');
        }
      },
    }
  );

  const { mutateAsync: deleteAttributeValueMutate } = useMutation(
    ItemService.deleteAttributeValue,
    {
      onSuccess: () => {
        queryClient.invalidateQueries('attributeGroups');
      },
    }
  );

  const handleCreateOrUpdateAttributeGroup = async () => {
    if (!newAttributeGroupName) return;

    const attributeGroupData: AttributeGroup.AsObject = {
      attributegroupid: editingAttributeGroupId || 0,
      attributegroupname: newAttributeGroupName,
      attributevaluesList: attributeValues, // Pass existing attribute values
      masterformatid: selectedDivision?.id || '',
    };

    try {
      await createOrUpdateAttributeGroupMutate(attributeGroupData);
    } catch (error) {
      console.error('Error creating or updating Attribute Group:', error);
    }
  };

  const handleEditAttributeGroup = (attributeGroup: AttributeGroup.AsObject) => {
    setNewAttributeGroupName(attributeGroup.attributegroupname);
    setEditingAttributeGroupId(attributeGroup.attributegroupid);

    // Ensure all attribute values have the correct group ID
    if (attributeGroup.attributevaluesList) {
      attributeGroup.attributevaluesList.forEach(attrValue => {
        attrValue.attributegroupid = attributeGroup.attributegroupid;
      });
    }

    // Find and set the selected division based on masterformatid
    const division = masterFormatDivision?.find(div => div.id === attributeGroup.masterformatid);
    setSelectedDivision(division || null);
  };

  const handleDeleteAttributeGroup = async (attributeGroupId: number) => {
    try {
      await deleteAttributeGroupMutate(attributeGroupId);
    } catch (error) {
      console.error('Error deleting Attribute Group:', error);
    }
  };

  const handleDeleteAttributeValue = async (attributeValueId: number) => {
    try {
      await deleteAttributeValueMutate(attributeValueId);
    } catch (error) {
      console.error('Error deleting Attribute Value:', error);
    }
  };

  /**
   * RESET button handler:
   * Clear out the editing group, but keep the user's division selection/filters.
   */
  const handleReset = () => {
    setEditingAttributeGroupId(null);
    setNewAttributeGroupName('');
    // We do NOT reset filterDivision or selectedDivision here,
    // ensuring that context remains.
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: theme.palette.background.default,
        color: theme.palette.text.primary,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          p: 2,
          pb: 0,
        }}
      >
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Attribute Group Name"
              value={newAttributeGroupName}
              onChange={e => setNewAttributeGroupName(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: theme.palette.background.paper,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="division-select-label">Division</InputLabel>
              <Select
                labelId="division-select-label"
                value={selectedDivision?.id || ''}
                label="Division"
                onChange={e => {
                  e.preventDefault();
                  const selectedValue = e.target.value as string;
                  const selected = masterFormatDivision?.find(
                    division => division.id === selectedValue
                  );
                  setSelectedDivision(selected || null);
                }}
                sx={{
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <MenuItem value="">
                  <em>Select Division</em>
                </MenuItem>
                {masterFormatDivision?.map(division => (
                  <MenuItem key={division.id} value={division.id}>
                    {`${division.divisionnumber} - ${division.divisionname}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCreateOrUpdateAttributeGroup();
                }}
                aria-label={
                  editingAttributeGroupId ? 'Update attribute group' : 'Add attribute group'
                }
              >
                {editingAttributeGroupId ? 'Update' : 'Add'}
              </Button>

              {editingAttributeGroupId !== null && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleReset}
                  aria-label="Reset selection"
                >
                  Reset
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          gap: 2,
          p: 2,
          pt: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <AttributesTable
            attributeGroups={filteredAttributeGroups || []}
            user={user as UB_AccountUser.AsObject}
            onEdit={handleEditAttributeGroup}
            onDelete={handleDeleteAttributeGroup}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <AttributeValuesTable
            attributeValues={attributeValues}
            user={user as UB_AccountUser.AsObject}
            onEdit={attrValue => handleOpenDialog(attrValue)}
            onDelete={attrValueId => handleDeleteAttributeValue(attrValueId)}
          />
        </Box>
      </Box>

      <Dialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        aria-labelledby="attribute-dialog-title"
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'hidden',
          },
        }}
      >
        <DialogContent sx={{ padding: 0, overflow: 'hidden', display: 'flex', flex: 1 }}>
          <ManageAttributeDialog
            isOpen={isDialogOpen}
            onClose={handleDialogClose}
            attributeValue={selectedAttributeValue}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default React.memo(CreateAttribute);
